import { useState, useMemo, useEffect } from 'react'
import { Package, Calculator, Copy, Sparkles, Ruler, Info, X } from 'lucide-react'
import { useLanguage } from '../contexts/LanguageContext'
import { Helmet } from 'react-helmet-async'
import FAQAccordion from '../components/FAQAccordion'

const DesiCalculator = () => {
  const { language } = useLanguage()
  const [length, setLength] = useState('')
  const [width, setWidth] = useState('')
  const [height, setHeight] = useState('')
  const [weight, setWeight] = useState('')
  const [result, setResult] = useState<number | null>(null)
  const [copied, setCopied] = useState(false)
  const [containerSize, setContainerSize] = useState(200)

  const translations = {
    tr: {
      title: 'Ücretsiz Desi Hesaplama Aracı',
      seoTitle: 'Ücretsiz Desi Hesaplama Aracı | İstanbul, Türkiye | Thinklyze',
      seoDescription: 'Kargo ve lojistik için ücretsiz desi hesaplama aracı. Paket boyutlarınızı girerek desi değerini hesaplayın. İstanbul ve Türkiye genelinde kullanılabilir. Tamamen ücretsiz.',
      seoKeywords: 'desi hesaplama, desi calculator, kargo desi hesaplama, lojistik desi, paket desi hesaplama, ücretsiz desi hesaplama, istanbul desi hesaplama',
      description: 'Kargo ve lojistik için paket desi değerini hesaplayın',
      lengthLabel: 'Uzunluk (cm)',
      widthLabel: 'Genişlik (cm)',
      heightLabel: 'Yükseklik (cm)',
      weightLabel: 'Ağırlık (kg) - İsteğe Bağlı',
      calculateButton: 'Desi Hesapla',
      clearButton: 'Temizle',
      resultTitle: 'Hesaplanan Desi Değeri',
      resultLabel: 'Desi',
      copyButton: 'Kopyala',
      copied: 'Kopyalandı!',
      formulaInfo: 'Desi = (Uzunluk × Genişlik × Yükseklik) / 3000',
      formulaNote: 'Not: Eğer ağırlık desi değerinden büyükse, kargo firması ağırlığı baz alır.',
      invalidInput: 'Lütfen geçerli boyut değerleri girin',
      faqTitle: 'Sık Sorulan Sorular',
      faqs: [
        {
          question: 'Desi nedir?',
          answer: 'Desi, kargo ve lojistik sektöründe kullanılan hacim ağırlığı birimidir. Paketlerin fiziksel ağırlığı yerine hacimsel ağırlığını hesaplamak için kullanılır.'
        },
        {
          question: 'Desi nasıl hesaplanır?',
          answer: 'Desi = (Uzunluk × Genişlik × Yükseklik) / 3000 formülü ile hesaplanır. Boyutlar santimetre (cm) cinsinden girilir.'
        },
        {
          question: 'Neden desi hesaplaması yapılır?',
          answer: 'Kargo firmaları, büyük ama hafif paketler için hacimsel ağırlığı kullanır. Bu sayede kargo maliyetleri daha adil bir şekilde hesaplanır.'
        },
        {
          question: 'Ağırlık neden isteğe bağlı?',
          answer: 'Kargo firmaları desi değeri ile fiziksel ağırlığı karşılaştırır ve hangisi daha büyükse onu baz alır. Bu yüzden ağırlık bilgisi de önemlidir.'
        },
        {
          question: 'Desi hesaplama aracı ücretsiz mi?',
          answer: 'Evet, desi hesaplama aracımız tamamen ücretsizdir. Sınırsız sayıda hesaplama yapabilirsiniz.'
        }
      ]
    },
    en: {
      title: 'Free Desi Calculator',
      seoTitle: 'Free Desi Calculator | Istanbul, Turkey | Thinklyze',
      seoDescription: 'Free desi calculator tool for cargo and logistics. Calculate desi value by entering your package dimensions. Available in Istanbul and throughout Turkey. Completely free.',
      seoKeywords: 'desi calculator, desi calculation, cargo desi calculator, logistics desi, package desi calculator, free desi calculator, istanbul desi calculator',
      description: 'Calculate package desi value for cargo and logistics',
      lengthLabel: 'Length (cm)',
      widthLabel: 'Width (cm)',
      heightLabel: 'Height (cm)',
      weightLabel: 'Weight (kg) - Optional',
      calculateButton: 'Calculate Desi',
      clearButton: 'Clear',
      resultTitle: 'Calculated Desi Value',
      resultLabel: 'Desi',
      copyButton: 'Copy',
      copied: 'Copied!',
      formulaInfo: 'Desi = (Length × Width × Height) / 3000',
      formulaNote: 'Note: If weight is greater than desi value, cargo companies use weight.',
      invalidInput: 'Please enter valid dimension values',
      faqTitle: 'Frequently Asked Questions',
      faqs: [
        {
          question: 'What is desi?',
          answer: 'Desi is a volumetric weight unit used in cargo and logistics. It is used to calculate the volumetric weight of packages instead of their physical weight.'
        },
        {
          question: 'How is desi calculated?',
          answer: 'Desi is calculated using the formula: Desi = (Length × Width × Height) / 3000. Dimensions are entered in centimeters (cm).'
        },
        {
          question: 'Why is desi calculation done?',
          answer: 'Cargo companies use volumetric weight for large but light packages. This allows cargo costs to be calculated more fairly.'
        },
        {
          question: 'Why is weight optional?',
          answer: 'Cargo companies compare the desi value with physical weight and use whichever is greater. That\'s why weight information is also important.'
        },
        {
          question: 'Is the desi calculator free?',
          answer: 'Yes, our desi calculator tool is completely free. You can make unlimited calculations.'
        }
      ]
    },
    ru: {
      title: 'Бесплатный калькулятор деси',
      seoTitle: 'Бесплатный калькулятор деси | Стамбул, Турция | Thinklyze',
      seoDescription: 'Бесплатный инструмент для расчета деси для грузовых перевозок и логистики. Рассчитайте значение деси, введя размеры вашей посылки. Доступно в Стамбуле и по всей Турции. Полностью бесплатно.',
      seoKeywords: 'калькулятор деси, расчет деси, калькулятор деси груз, логистика деси, расчет деси посылки, бесплатный калькулятор деси, стамбул калькулятор деси',
      description: 'Рассчитайте значение деси посылки для грузовых перевозок и логистики',
      lengthLabel: 'Длина (см)',
      widthLabel: 'Ширина (см)',
      heightLabel: 'Высота (см)',
      weightLabel: 'Вес (кг) - необязательно',
      calculateButton: 'Рассчитать деси',
      clearButton: 'Очистить',
      resultTitle: 'Рассчитанное значение деси',
      resultLabel: 'Деси',
      copyButton: 'Копировать',
      copied: 'Скопировано!',
      formulaInfo: 'Деси = (Длина × Ширина × Высота) / 3000',
      formulaNote: 'Примечание: Если вес больше значения деси, грузовые компании используют вес.',
      invalidInput: 'Пожалуйста, введите действительные значения размеров',
      faqTitle: 'Часто задаваемые вопросы',
      faqs: [
        {
          question: 'Что такое деси?',
          answer: 'Деси - это единица объемного веса, используемая в грузовых перевозках и логистике. Используется для расчета объемного веса посылок вместо их физического веса.'
        },
        {
          question: 'Как рассчитывается деси?',
          answer: 'Деси рассчитывается по формуле: Деси = (Длина × Ширина × Высота) / 3000. Размеры вводятся в сантиметрах (см).'
        },
        {
          question: 'Зачем рассчитывается деси?',
          answer: 'Грузовые компании используют объемный вес для больших, но легких посылок. Это позволяет более справедливо рассчитывать стоимость грузовых перевозок.'
        },
        {
          question: 'Почему вес необязателен?',
          answer: 'Грузовые компании сравнивают значение деси с физическим весом и используют то, которое больше. Поэтому информация о весе также важна.'
        },
        {
          question: 'Калькулятор деси бесплатный?',
          answer: 'Да, наш инструмент калькулятора деси полностью бесплатный. Вы можете делать неограниченное количество расчетов.'
        }
      ]
    }
  }

  const t = translations[language as keyof typeof translations] || translations.tr

  // SEO Schema Markup
  const breadcrumbSchema = useMemo(() => ({
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: language === 'tr' ? 'Ana Sayfa' : language === 'en' ? 'Home' : 'Главная',
        item: 'https://thinklyze.com/'
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: language === 'tr' ? 'Hizmetler' : language === 'en' ? 'Services' : 'Услуги',
        item: 'https://thinklyze.com/services'
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: t.title,
        item: 'https://thinklyze.com/desi-calculator'
      }
    ]
  }), [language, t.title])

  const howToSchema = useMemo(() => ({
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name: t.title,
    description: t.description,
    step: [
      {
        '@type': 'HowToStep',
        position: 1,
        name: language === 'tr' ? 'Paket boyutlarını girin' : language === 'en' ? 'Enter package dimensions' : 'Введите размеры посылки',
        text: language === 'tr' ? 'Uzunluk, genişlik ve yükseklik değerlerini santimetre (cm) cinsinden girin.' : language === 'en' ? 'Enter length, width, and height values in centimeters (cm).' : 'Введите значения длины, ширины и высоты в сантиметрах (см).'
      },
      {
        '@type': 'HowToStep',
        position: 2,
        name: language === 'tr' ? 'İsteğe bağlı ağırlık ekleyin' : language === 'en' ? 'Add optional weight' : 'Добавьте необязательный вес',
        text: language === 'tr' ? 'Paketin fiziksel ağırlığını kilogram (kg) cinsinden ekleyebilirsiniz.' : language === 'en' ? 'You can add the physical weight of the package in kilograms (kg).' : 'Вы можете добавить физический вес посылки в килограммах (кг).'
      },
      {
        '@type': 'HowToStep',
        position: 3,
        name: language === 'tr' ? 'Desi değerini hesaplayın' : language === 'en' ? 'Calculate desi value' : 'Рассчитайте значение деси',
        text: language === 'tr' ? '\'Desi Hesapla\' butonuna tıklayın ve desi değerini görüntüleyin.' : language === 'en' ? 'Click the \'Calculate Desi\' button and view the desi value.' : 'Нажмите кнопку \'Рассчитать деси\' и просмотрите значение деси.'
      }
    ]
  }), [language, t.title, t.description])

  const faqSchema = useMemo(() => {
    if (!t.faqs || t.faqs.length === 0) return null
    return {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: t.faqs.map((faq: any) => ({
        '@type': 'Question',
        name: faq.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: faq.answer
        }
      }))
    }
  }, [t.faqs])

  const webPageSchema = useMemo(() => ({
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: t.seoTitle || t.title,
    description: t.seoDescription || t.description,
    url: 'https://thinklyze.com/desi-calculator',
    inLanguage: language === 'tr' ? 'tr-TR' : language === 'en' ? 'en-US' : 'ru-RU',
    about: {
      '@type': 'Thing',
      name: 'Desi Calculator',
      description: t.description
    },
    provider: {
      '@type': 'Organization',
      name: 'Thinklyze',
      url: 'https://thinklyze.com',
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Bakırköy',
        addressRegion: 'İstanbul',
        addressCountry: 'TR'
      }
    }
  }), [language, t])

  const calculateDesi = () => {
    const l = parseFloat(String(length).replace(',', '.'))
    const w = parseFloat(String(width).replace(',', '.'))
    const h = parseFloat(String(height).replace(',', '.'))

    if (!l || !w || !h || l <= 0 || w <= 0 || h <= 0 || isNaN(l) || isNaN(w) || isNaN(h)) {
      alert(t.invalidInput)
      return
    }

    // Desi = (Uzunluk × Genişlik × Yükseklik) / 3000
    const desi = (l * w * h) / 3000
    setResult(desi)
    setCopied(false)
  }

  const clearAll = () => {
    setLength('')
    setWidth('')
    setHeight('')
    setWeight('')
    setResult(null)
    setCopied(false)
  }

  const copyToClipboard = async () => {
    if (result === null) return

    const text = `${t.resultLabel}: ${result.toFixed(2)}`
    try {
      await navigator.clipboard.writeText(text)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (err) {
      console.error('Kopyalama hatası:', err)
    }
  }

  // Update container size based on window width
  useEffect(() => {
    const updateSize = () => {
      if (window.innerWidth < 640) {
        setContainerSize(150)
      } else if (window.innerWidth < 1024) {
        setContainerSize(180)
      } else {
        setContainerSize(200)
      }
    }
    
    updateSize()
    window.addEventListener('resize', updateSize)
    return () => window.removeEventListener('resize', updateSize)
  }, [])

  // Auto-recalculate when dimensions change (if all required fields are filled)
  useEffect(() => {
    const l = parseFloat(String(length).replace(',', '.'))
    const w = parseFloat(String(width).replace(',', '.'))
    const h = parseFloat(String(height).replace(',', '.'))

    // Only auto-calculate if all required fields have valid values
    if (length !== '' && width !== '' && height !== '' && !isNaN(l) && !isNaN(w) && !isNaN(h) && l > 0 && w > 0 && h > 0) {
      // Desi = (Uzunluk × Genişlik × Yükseklik) / 3000
      const desi = (l * w * h) / 3000
      setResult(desi)
      setCopied(false)
    } else if (length === '' || width === '' || height === '' || isNaN(l) || isNaN(w) || isNaN(h) || l <= 0 || w <= 0 || h <= 0) {
      // Clear result if fields are invalid
      setResult(null)
      setCopied(false)
    }
  }, [length, width, height])

  const finalWeight = useMemo(() => {
    if (result === null) return null
    const w = parseFloat(String(weight).replace(',', '.'))
    if (w && w > 0 && !isNaN(w) && w > result) {
      return w
    }
    return result
  }, [result, weight])

  return (
    <>
      <Helmet>
        <title>{t.seoTitle || t.title} | Thinklyze</title>
        <meta name="description" content={t.seoDescription || t.description} />
        <meta name="keywords" content={t.seoKeywords} />
        <link rel="canonical" href="https://thinklyze.com/desi-calculator" />
        
        {/* Open Graph Tags */}
        <meta property="og:title" content={t.seoTitle || t.title} />
        <meta property="og:description" content={t.seoDescription || t.description} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://thinklyze.com/desi-calculator" />
        <meta property="og:image" content="https://thinklyze.com/images/logo_siyah.webp" />
        <meta property="og:locale" content={language === 'tr' ? 'tr_TR' : language === 'en' ? 'en_US' : 'ru_RU'} />
        
        {/* Twitter Card Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={t.seoTitle || t.title} />
        <meta name="twitter:description" content={t.seoDescription || t.description} />
        <meta name="twitter:image" content="https://thinklyze.com/images/logo_siyah.webp" />
        
        {/* Geo Tags */}
        <meta name="geo.region" content="TR-34" />
        <meta name="geo.placename" content="İstanbul" />
        <meta name="geo.position" content="41.0082;28.9784" />
        <meta name="ICBM" content="41.0082, 28.9784" />
      </Helmet>
      
      {/* Structured Data */}
      <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(howToSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(webPageSchema)}</script>
      {faqSchema && <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>}
      
      <div className="pt-16 bg-[#f9f9f9] relative min-h-screen">
        {/* Background Grid Pattern */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#e5e7eb_1px,transparent_1px),linear-gradient(to_bottom,#e5e7eb_1px,transparent_1px)] bg-[size:80px_80px] [mask-image:radial-gradient(ellipse_60%_40%_at_50%_10%,#000_20%,transparent_70%)] opacity-40 pointer-events-none"></div>
        
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 lg:py-16 relative z-10">
          {/* Header */}
          <div className="text-center mb-6 sm:mb-8 md:mb-12">
            <div className="inline-flex items-center gap-2 mb-3 sm:mb-4 md:mb-6">
              <Sparkles className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-blue-600" />
              <span className="text-[10px] sm:text-xs md:text-[13px] font-medium text-gray-600 bg-white px-2.5 sm:px-3 md:px-4 py-1 sm:py-1.5 rounded-full border border-gray-200 shadow-sm">
                {language === 'tr' ? 'Ücretsiz Araç' : language === 'en' ? 'Free Tool' : 'Бесплатный инструмент'}
              </span>
            </div>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-3 md:gap-4 mb-3 sm:mb-4 md:mb-6">
              <div className="relative">
                <div className="absolute inset-0 bg-blue-600 opacity-20 blur-xl rounded-full"></div>
                <Package className="w-10 h-10 sm:w-12 sm:h-12 md:w-16 md:h-16 text-blue-600 relative z-10" />
              </div>
              <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 leading-tight px-2 sm:px-0">
                {t.title}
              </h1>
            </div>
            <p className="text-sm sm:text-base md:text-lg lg:text-xl text-gray-700 max-w-2xl mx-auto mb-2 sm:mb-3 md:mb-4 leading-relaxed px-4 sm:px-6 md:px-0">
              {t.description}
            </p>
            <p className="text-[10px] sm:text-xs md:text-sm text-gray-500 max-w-2xl mx-auto px-4 sm:px-6 md:px-0">
              {language === 'tr' 
                ? 'İstanbul, Türkiye\'de dijital pazarlama profesyonelleri için ücretsiz araç' 
                : language === 'en' 
                ? 'Free tool for digital marketing professionals in Istanbul, Turkey' 
                : 'Бесплатный инструмент для специалистов по цифровому маркетингу в Стамбуле, Турция'}
            </p>
          </div>

          {/* Main Card */}
          <div className="bg-white rounded-xl sm:rounded-2xl shadow-[0_4px_20px_rgba(0,0,0,0.08)] p-4 sm:p-5 md:p-6 lg:p-8 xl:p-10 mb-4 sm:mb-6 md:mb-8 border border-gray-100 hover:shadow-[0_8px_30px_rgba(0,0,0,0.12)] transition-all duration-300">
            {/* Formula Info */}
            <div className="mb-4 sm:mb-5 md:mb-6 lg:mb-8 p-3 sm:p-4 bg-blue-50 border-2 border-blue-200 rounded-lg sm:rounded-xl flex items-start gap-2 sm:gap-3">
              <Info className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="text-xs sm:text-sm font-semibold text-blue-900 mb-1 leading-snug">{t.formulaInfo}</p>
                <p className="text-[10px] sm:text-xs text-blue-700 leading-snug">{t.formulaNote}</p>
              </div>
            </div>

            {/* Main Content Grid - Form and Preview */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 lg:gap-8 mb-6 sm:mb-8">
              {/* Left Side - Form */}
              <div className="order-2 lg:order-1">
                {/* Dimensions Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 md:gap-6 mb-4 sm:mb-6 md:mb-8">
              <div>
                <label htmlFor="length" className="flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm font-semibold text-gray-900 mb-1.5 sm:mb-2 md:mb-3">
                  <Ruler className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-blue-600 flex-shrink-0" />
                  <span className="truncate">{t.lengthLabel} *</span>
                </label>
                <input
                  type="number"
                  id="length"
                  value={length}
                  onChange={(e) => {
                    const value = e.target.value.replace(',', '.')
                    setLength(value)
                  }}
                  placeholder="0"
                  min="0"
                  step="0.1"
                  className="w-full px-3 sm:px-4 py-2.5 sm:py-3 md:py-4 border-2 border-gray-200 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 text-sm sm:text-base md:text-lg bg-gray-50 focus:bg-white"
                />
              </div>
              <div>
                <label htmlFor="width" className="flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm font-semibold text-gray-900 mb-1.5 sm:mb-2 md:mb-3">
                  <Ruler className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-blue-600 flex-shrink-0" />
                  <span className="truncate">{t.widthLabel} *</span>
                </label>
                <input
                  type="number"
                  id="width"
                  value={width}
                  onChange={(e) => {
                    const value = e.target.value.replace(',', '.')
                    setWidth(value)
                  }}
                  placeholder="0"
                  min="0"
                  step="0.1"
                  className="w-full px-3 sm:px-4 py-2.5 sm:py-3 md:py-4 border-2 border-gray-200 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 text-sm sm:text-base md:text-lg bg-gray-50 focus:bg-white"
                />
              </div>
              <div>
                <label htmlFor="height" className="flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm font-semibold text-gray-900 mb-1.5 sm:mb-2 md:mb-3">
                  <Ruler className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-blue-600 flex-shrink-0" />
                  <span className="truncate">{t.heightLabel} *</span>
                </label>
                <input
                  type="number"
                  id="height"
                  value={height}
                  onChange={(e) => {
                    const value = e.target.value.replace(',', '.')
                    setHeight(value)
                  }}
                  placeholder="0"
                  min="0"
                  step="0.1"
                  className="w-full px-3 sm:px-4 py-2.5 sm:py-3 md:py-4 border-2 border-gray-200 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 text-sm sm:text-base md:text-lg bg-gray-50 focus:bg-white"
                />
              </div>
            </div>

                {/* Weight Input */}
                <div className="mb-4 sm:mb-5 md:mb-6 lg:mb-8">
                  <label htmlFor="weight" className="flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm font-semibold text-gray-900 mb-1.5 sm:mb-2 md:mb-3">
                    <Package className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-blue-600 flex-shrink-0" />
                    <span className="truncate">{t.weightLabel}</span>
                  </label>
                  <input
                    type="number"
                    id="weight"
                    value={weight}
                    onChange={(e) => {
                      const value = e.target.value.replace(',', '.')
                      setWeight(value)
                    }}
                    placeholder="0"
                    min="0"
                    step="0.1"
                    className="w-full px-3 sm:px-4 py-2.5 sm:py-3 md:py-4 border-2 border-gray-200 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 text-sm sm:text-base md:text-lg bg-gray-50 focus:bg-white"
                  />
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row gap-2.5 sm:gap-3 md:gap-4">
                  <button
                    onClick={calculateDesi}
                    className="flex-1 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-semibold py-2.5 sm:py-3 md:py-4 px-4 sm:px-5 md:px-6 rounded-lg sm:rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all duration-300 flex items-center justify-center gap-2 sm:gap-3 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 text-xs sm:text-sm md:text-base"
                  >
                    <Calculator className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
                    <span className="truncate">{t.calculateButton}</span>
                  </button>
                  <button
                    onClick={clearAll}
                    className="px-4 sm:px-5 md:px-6 py-2.5 sm:py-3 md:py-4 bg-gray-100 text-gray-700 font-medium rounded-lg sm:rounded-xl hover:bg-gray-200 transition-colors duration-200 flex items-center justify-center gap-2"
                  >
                    <X className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
                    <span className="truncate">{t.clearButton}</span>
                  </button>
                </div>
              </div>

              {/* Right Side - 3D Box Preview */}
              <div className="lg:sticky lg:top-24 h-fit order-first lg:order-last">
                <div className="bg-gradient-to-br from-gray-50 to-blue-50 rounded-xl sm:rounded-2xl p-4 sm:p-6 md:p-8 border-2 border-gray-200">
                  <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-3 sm:mb-4 md:mb-6 text-center">
                    {language === 'tr' ? 'Paket Önizlemesi' : language === 'en' ? 'Package Preview' : 'Предварительный просмотр посылки'}
                  </h3>
                  
                  {/* 3D Box Container */}
                  <div className="relative w-full aspect-square max-w-[200px] sm:max-w-[250px] md:max-w-sm mx-auto mb-3 sm:mb-4 md:mb-6" style={{ perspective: '1500px', perspectiveOrigin: '50% 50%' }}>
                    <div 
                      className="relative w-full h-full flex items-center justify-center"
                      style={{
                        transformStyle: 'preserve-3d',
                        transform: 'rotateX(-25deg) rotateY(35deg)',
                        transition: 'transform 0.6s cubic-bezier(0.4, 0, 0.2, 1)'
                      }}
                    >
                      {/* 3D Box */}
                      {length && width && height && parseFloat(length) > 0 && parseFloat(width) > 0 && parseFloat(height) > 0 ? (
                        <div className="relative" style={{ transformStyle: 'preserve-3d', width: '100%', height: '100%', maxWidth: '200px', maxHeight: '200px' }}>
                          {(() => {
                            const l = parseFloat(String(length).replace(',', '.'))
                            const w = parseFloat(String(width).replace(',', '.'))
                            const h = parseFloat(String(height).replace(',', '.'))
                            const maxDim = Math.max(l, w, h)
                            const scale = containerSize / maxDim
                            const scaledL = l * scale
                            const scaledW = w * scale
                            const scaledH = h * scale
                            const depth = scaledL
                            
                            return (
                              <>
                                {/* Front Face */}
                                <div 
                                  className="absolute bg-gradient-to-br from-amber-50 via-amber-100 to-amber-200 border-2 border-amber-300 shadow-2xl"
                                  style={{
                                    width: `${scaledW}px`,
                                    height: `${scaledH}px`,
                                    transform: `translateZ(${depth / 2}px)`,
                                    left: `calc(50% - ${scaledW / 2}px)`,
                                    top: `calc(50% - ${scaledH / 2}px)`,
                                    backfaceVisibility: 'hidden',
                                    borderRadius: '4px',
                                  }}
                                >
                                  <div className="absolute inset-0 flex flex-col items-center justify-center p-2">
                                    <div className="text-center">
                                      <div className="text-xs sm:text-sm font-extrabold text-amber-900 tracking-wide" style={{ textShadow: '0 1px 2px rgba(0,0,0,0.1)' }}>THINKLYZE</div>
                                    </div>
                                  </div>
                                  {/* Tape effect */}
                                  <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-12 h-2 bg-amber-400 opacity-60" style={{ clipPath: 'polygon(0 0, 100% 0, 95% 100%, 5% 100%)' }}></div>
                                </div>
                                
                                {/* Back Face */}
                                <div 
                                  className="absolute bg-gradient-to-br from-amber-100 via-amber-150 to-amber-200 border-2 border-amber-300 shadow-2xl"
                                  style={{
                                    width: `${scaledW}px`,
                                    height: `${scaledH}px`,
                                    transform: `rotateY(180deg) translateZ(${depth / 2}px)`,
                                    left: `calc(50% - ${scaledW / 2}px)`,
                                    top: `calc(50% - ${scaledH / 2}px)`,
                                    backfaceVisibility: 'hidden',
                                    borderRadius: '4px',
                                  }}
                                >
                                  {/* Vertical lines for cardboard texture */}
                                  <div className="absolute inset-0 opacity-15">
                                    <div className="absolute left-1/4 top-0 bottom-0 w-px bg-amber-600"></div>
                                    <div className="absolute left-1/2 top-0 bottom-0 w-px bg-amber-600"></div>
                                    <div className="absolute left-3/4 top-0 bottom-0 w-px bg-amber-600"></div>
                                  </div>
                                </div>
                                
                                {/* Right Face */}
                                <div 
                                  className="absolute bg-gradient-to-br from-amber-200 via-amber-300 to-amber-400 border-2 border-amber-500 shadow-2xl"
                                  style={{
                                    width: `${depth}px`,
                                    height: `${scaledH}px`,
                                    transform: `rotateY(90deg) translateZ(${scaledW / 2}px)`,
                                    left: `calc(50% - ${depth / 2}px)`,
                                    top: `calc(50% - ${scaledH / 2}px)`,
                                    backfaceVisibility: 'hidden',
                                    borderRadius: '4px',
                                  }}
                                >
                                  {/* Vertical lines for cardboard texture */}
                                  <div className="absolute inset-0 opacity-20">
                                    <div className="absolute left-1/3 top-0 bottom-0 w-px bg-amber-600"></div>
                                    <div className="absolute left-2/3 top-0 bottom-0 w-px bg-amber-600"></div>
                                  </div>
                                </div>
                                
                                {/* Left Face */}
                                <div 
                                  className="absolute bg-gradient-to-br from-amber-250 via-amber-350 to-amber-450 border-2 border-amber-550 shadow-2xl"
                                  style={{
                                    width: `${depth}px`,
                                    height: `${scaledH}px`,
                                    transform: `rotateY(-90deg) translateZ(${scaledW / 2}px)`,
                                    left: `calc(50% - ${depth / 2}px)`,
                                    top: `calc(50% - ${scaledH / 2}px)`,
                                    backfaceVisibility: 'hidden',
                                    borderRadius: '4px',
                                    background: 'linear-gradient(to bottom right, rgb(252, 211, 77), rgb(251, 191, 36), rgb(245, 158, 11))',
                                  }}
                                >
                                  {/* Vertical lines for cardboard texture */}
                                  <div className="absolute inset-0 opacity-20">
                                    <div className="absolute left-1/3 top-0 bottom-0 w-px bg-amber-700"></div>
                                    <div className="absolute left-2/3 top-0 bottom-0 w-px bg-amber-700"></div>
                                  </div>
                                </div>
                                
                                {/* Top Face */}
                                <div 
                                  className="absolute bg-gradient-to-br from-amber-300 via-amber-400 to-amber-500 border-2 border-amber-600 shadow-2xl"
                                  style={{
                                    width: `${scaledW}px`,
                                    height: `${depth}px`,
                                    transform: `rotateX(90deg) translateZ(${scaledH / 2}px)`,
                                    left: `calc(50% - ${scaledW / 2}px)`,
                                    top: `calc(50% - ${depth / 2}px)`,
                                    backfaceVisibility: 'hidden',
                                    borderRadius: '4px',
                                  }}
                                >
                                  {/* Horizontal lines for cardboard texture */}
                                  <div className="absolute inset-0 opacity-20">
                                    <div className="absolute top-1/3 left-0 right-0 h-px bg-amber-700"></div>
                                    <div className="absolute top-2/3 left-0 right-0 h-px bg-amber-700"></div>
                                  </div>
                                </div>
                                
                                {/* Bottom Face */}
                                <div 
                                  className="absolute bg-gradient-to-br from-amber-400 via-amber-500 to-amber-600 border-2 border-amber-700 shadow-2xl"
                                  style={{
                                    width: `${scaledW}px`,
                                    height: `${depth}px`,
                                    transform: `rotateX(-90deg) translateZ(${scaledH / 2}px)`,
                                    left: `calc(50% - ${scaledW / 2}px)`,
                                    top: `calc(50% - ${depth / 2}px)`,
                                    backfaceVisibility: 'hidden',
                                    borderRadius: '4px',
                                  }}
                                >
                                  {/* Horizontal lines for cardboard texture */}
                                  <div className="absolute inset-0 opacity-25">
                                    <div className="absolute top-1/3 left-0 right-0 h-px bg-amber-800"></div>
                                    <div className="absolute top-2/3 left-0 right-0 h-px bg-amber-800"></div>
                                  </div>
                                </div>
                              </>
                            )
                          })()}
                        </div>
                      ) : (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="text-center">
                            <Package className="w-16 h-16 sm:w-20 sm:h-20 text-gray-300 mx-auto mb-3" />
                            <p className="text-sm text-gray-400">
                              {language === 'tr' 
                                ? 'Boyutları girin' 
                                : language === 'en' 
                                ? 'Enter dimensions' 
                                : 'Введите размеры'}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Result Display */}
                  {result !== null && (
                    <div className="mb-3 sm:mb-4 md:mb-6">
                      <div className="bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl p-3 sm:p-4 md:p-6 shadow-lg">
                        <div className="text-center">
                          <div className="text-xs sm:text-sm font-semibold text-blue-100 mb-1 sm:mb-2">
                            {t.resultLabel}
                          </div>
                          <div className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-extrabold text-white mb-1 sm:mb-2">
                            {finalWeight?.toFixed(2)}
                          </div>
                          {weight && parseFloat(String(weight).replace(',', '.')) > 0 && parseFloat(String(weight).replace(',', '.')) > result && (
                            <div className="text-[10px] sm:text-xs md:text-sm text-blue-100 bg-blue-600/30 px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg inline-block mt-1 sm:mt-2">
                              {language === 'tr' 
                                ? `Ağırlık (${parseFloat(String(weight).replace(',', '.')).toFixed(2)} kg) kullanılıyor`
                                : language === 'en'
                                ? `Weight (${parseFloat(String(weight).replace(',', '.')).toFixed(2)} kg) is used`
                                : `Используется вес (${parseFloat(String(weight).replace(',', '.')).toFixed(2)} кг)`}
                            </div>
                          )}
                          {result && (!weight || parseFloat(String(weight).replace(',', '.')) <= 0 || parseFloat(String(weight).replace(',', '.')) <= result) && (
                            <div className="text-[10px] sm:text-xs md:text-sm text-blue-100 bg-blue-600/30 px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg inline-block mt-1 sm:mt-2">
                              {language === 'tr' 
                                ? `Desi değeri: ${result.toFixed(2)}`
                                : language === 'en'
                                ? `Desi value: ${result.toFixed(2)}`
                                : `Значение деси: ${result.toFixed(2)}`}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Dimensions Display */}
                  {(length || width || height) && (
                    <div className="space-y-1.5 sm:space-y-2 text-xs sm:text-sm">
                      {length && parseFloat(String(length).replace(',', '.')) > 0 && (
                        <div className="flex items-center justify-between bg-white px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg border border-gray-200 shadow-sm">
                          <span className="text-gray-600 font-medium flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm">
                            <Ruler className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-blue-600 flex-shrink-0" />
                            <span className="truncate">{t.lengthLabel}:</span>
                          </span>
                          <span className="text-gray-900 font-bold text-xs sm:text-sm ml-2">{parseFloat(String(length).replace(',', '.'))} cm</span>
                        </div>
                      )}
                      {width && parseFloat(String(width).replace(',', '.')) > 0 && (
                        <div className="flex items-center justify-between bg-white px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg border border-gray-200 shadow-sm">
                          <span className="text-gray-600 font-medium flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm">
                            <Ruler className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-blue-600 flex-shrink-0" />
                            <span className="truncate">{t.widthLabel}:</span>
                          </span>
                          <span className="text-gray-900 font-bold text-xs sm:text-sm ml-2">{parseFloat(String(width).replace(',', '.'))} cm</span>
                        </div>
                      )}
                      {height && parseFloat(String(height).replace(',', '.')) > 0 && (
                        <div className="flex items-center justify-between bg-white px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg border border-gray-200 shadow-sm">
                          <span className="text-gray-600 font-medium flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm">
                            <Ruler className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-blue-600 flex-shrink-0" />
                            <span className="truncate">{t.heightLabel}:</span>
                          </span>
                          <span className="text-gray-900 font-bold text-xs sm:text-sm ml-2">{parseFloat(String(height).replace(',', '.'))} cm</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Copy Button Section - Only show if result exists */}
          {result !== null && (
            <div className="bg-white rounded-xl sm:rounded-2xl shadow-[0_4px_20px_rgba(0,0,0,0.08)] p-4 sm:p-5 md:p-6 border border-gray-100 mb-4 sm:mb-6 md:mb-8">
              <button
                onClick={copyToClipboard}
                className={`w-full font-semibold py-2.5 sm:py-3 md:py-4 px-4 sm:px-5 md:px-6 rounded-lg sm:rounded-xl transition-all duration-300 flex items-center justify-center gap-2 sm:gap-3 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 text-xs sm:text-sm md:text-base ${
                  copied 
                    ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white' 
                    : 'bg-gradient-to-r from-gray-900 to-gray-800 text-white hover:from-gray-800 hover:to-gray-700'
                }`}
              >
                <Copy className={`w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0 ${copied ? 'animate-bounce' : ''}`} />
                <span className="truncate">{copied ? t.copied : t.copyButton}</span>
              </button>
            </div>
          )}

          {/* FAQ Section */}
          {t.faqs && t.faqs.length > 0 && (
            <div className="bg-white rounded-xl sm:rounded-2xl shadow-[0_4px_20px_rgba(0,0,0,0.08)] p-4 sm:p-5 md:p-6 lg:p-8 xl:p-10 mt-4 sm:mt-6 md:mt-8 border border-gray-100">
              <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-5 md:mb-6 lg:mb-8">
                <div className="w-7 h-7 sm:w-8 sm:h-8 md:w-10 md:h-10 rounded-full bg-gradient-to-br from-blue-600 to-blue-700 flex items-center justify-center flex-shrink-0">
                  <Info className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-white" />
                </div>
                <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900">{t.faqTitle}</h2>
              </div>
              <FAQAccordion 
                faqs={t.faqs.map((faq: any) => ({
                  question: faq.question,
                  answer: faq.answer
                }))}
              />
            </div>
          )}
        </div>
      </div>
    </>
  )
}

export default DesiCalculator

